/**
 * Client Service Hook
 *
 * Custom hook that integrates Apollo GraphQL with Zustand store
 * for client management operations. Provides a unified interface
 * for all client-related data operations.
 */

import { useCallback, useEffect } from "react";
import { useMutation, useApolloClient } from "@apollo/client";
import {
  LIST_CLIENTS,
  GET_CLIENT,
  GET_CLIENT_STATISTICS,
  CREATE_CLIENT,
  UPDATE_CLIENT,
  SUSPEND_CLIENT,
  ACTIVATE_CLIENT,
  type CreateClientInput,
  type UpdateClientInput,
} from "../services/client-api";
import {
  useClientStore,
  useSelectedClient,
  useClientActions,
  useClientStatistics,
} from "../stores/client-store";

interface ClientServiceReturn {
  // State
  clients: any[];
  clientsLoading: boolean;
  clientsError: string | null;
  hasMore: boolean;
  nextToken: string | null;
  selectedClient: any | null;
  selectedClientLoading: boolean;
  selectedClientError: string | null;

  // Actions
  loadClients: (variables?: any) => Promise<void>;
  loadClient: (clientId: string) => Promise<void>;
  loadClientStatistics: (clientId: string) => Promise<void>;
  createClient: (input: CreateClientInput) => Promise<any>;
  updateClient: (input: UpdateClientInput) => Promise<any>;
  suspendClient: (clientId: string) => Promise<any>;
  activateClient: (clientId: string) => Promise<any>;
  refreshClients: () => void;
  clearSelectedClient: () => void;

  // Utilities
  resetErrors: () => void;
}

export function useClientService(): ClientServiceReturn {
  const apolloClient = useApolloClient();
  const {
    setClients,
    addClients,
    setClientsLoading,
    setClientsError,
    setSelectedClient,
    setSelectedClientLoading,
    setSelectedClientError,
    resetClientList,
    resetSelectedClient,
    resetErrors,
  } = useClientActions();

  const { setClientStatistics, setStatisticsLoading, setStatisticsError } =
    useClientStatistics();

  const { clients, clientsLoading, clientsError, hasMore, nextToken } =
    useClientStore();
  const {
    client: selectedClient,
    loading: selectedClientLoading,
    error: selectedClientError,
  } = useSelectedClient();

  // GraphQL mutations
  const [createClientMutation] = useMutation(CREATE_CLIENT);
  const [updateClientMutation] = useMutation(UPDATE_CLIENT);
  const [suspendClientMutation] = useMutation(SUSPEND_CLIENT);
  const [activateClientMutation] = useMutation(ACTIVATE_CLIENT);

  /**
   * Load clients list with pagination
   */
  const loadClients = useCallback(
    async (reset: boolean = false): Promise<void> => {
      try {
        setClientsLoading(true);
        resetErrors();

        const variables = {
          limit: 50,
          nextToken: reset ? null : nextToken,
        };

        const { data } = await apolloClient.query({
          query: LIST_CLIENTS,
          variables,
          fetchPolicy: reset ? "network-only" : "cache-first",
        });

        if (data?.listClients) {
          if (reset) {
            setClients(data.listClients.items, data.listClients.nextToken);
          } else {
            addClients(data.listClients.items, data.listClients.nextToken);
          }
        }
      } catch (error) {
        console.error("Error loading clients:", error);
        setClientsError(
          error instanceof Error ? error.message : "Failed to load clients"
        );
      } finally {
        setClientsLoading(false);
      }
    },
    [
      apolloClient,
      nextToken,
      setClients,
      addClients,
      setClientsLoading,
      setClientsError,
      resetErrors,
    ]
  );

  /**
   * Load a specific client by ID
   */
  const loadClient = useCallback(
    async (clientId: string): Promise<void> => {
      try {
        setSelectedClientLoading(true);
        resetErrors();

        const { data } = await apolloClient.query({
          query: GET_CLIENT,
          variables: { clientId },
          fetchPolicy: "cache-first",
        });

        if (data?.getClient) {
          setSelectedClient(data.getClient);
        } else {
          setSelectedClientError("Client not found");
        }
      } catch (error) {
        console.error("Error loading client:", error);
        setSelectedClientError(
          error instanceof Error ? error.message : "Failed to load client"
        );
      } finally {
        setSelectedClientLoading(false);
      }
    },
    [
      apolloClient,
      setSelectedClient,
      setSelectedClientLoading,
      setSelectedClientError,
      resetErrors,
    ]
  );

  /**
   * Load client statistics
   */
  const loadClientStatistics = useCallback(
    async (clientId: string, filter?: any): Promise<void> => {
      try {
        setStatisticsLoading(true);
        resetErrors();

        const { data } = await apolloClient.query({
          query: GET_CLIENT_STATISTICS,
          variables: { clientId, filter },
          fetchPolicy: "cache-first",
        });

        if (data?.getClientStatistics) {
          setClientStatistics(clientId, data.getClientStatistics);
        }
      } catch (error) {
        console.error("Error loading client statistics:", error);
        setStatisticsError(
          error instanceof Error ? error.message : "Failed to load statistics"
        );
      } finally {
        setStatisticsLoading(false);
      }
    },
    [
      apolloClient,
      setClientStatistics,
      setStatisticsLoading,
      setStatisticsError,
      resetErrors,
    ]
  );

  /**
   * Create a new client
   */
  const createClient = useCallback(
    async (input: CreateClientInput): Promise<any> => {
      try {
        useClientStore.getState().setCreateClientLoading(true);
        resetErrors();

        const { data } = await createClientMutation({
          variables: { input },
          refetchQueries: [{ query: LIST_CLIENTS }],
        });

        if (data?.createClient) {
          // Add the new client to the store
          setClients([data.createClient, ...clients], nextToken);
          return data.createClient;
        }
      } catch (error) {
        console.error("Error creating client:", error);
        useClientStore
          .getState()
          .setCreateClientError(
            error instanceof Error ? error.message : "Failed to create client"
          );
        throw error;
      } finally {
        useClientStore.getState().setCreateClientLoading(false);
      }
    },
    [createClientMutation, clients, nextToken, setClients, resetErrors]
  );

  /**
   * Update a client
   */
  const updateClient = useCallback(
    async (input: UpdateClientInput): Promise<any> => {
      try {
        useClientStore.getState().setUpdateClientLoading(true);
        resetErrors();

        const { data } = await updateClientMutation({
          variables: { input },
          refetchQueries: [
            { query: LIST_CLIENTS },
            { query: GET_CLIENT, variables: { clientId: input.clientId } },
          ],
        });

        if (data?.updateClient) {
          // Update the client in the store
          const updatedClients = clients.map((client) =>
            client.id === data.updateClient.id ? data.updateClient : client
          );
          setClients(updatedClients, nextToken);

          // Update selected client if it's the same one
          if (selectedClient?.id === data.updateClient.id) {
            setSelectedClient(data.updateClient);
          }

          return data.updateClient;
        }
      } catch (error) {
        console.error("Error updating client:", error);
        useClientStore
          .getState()
          .setUpdateClientError(
            error instanceof Error ? error.message : "Failed to update client"
          );
        throw error;
      } finally {
        useClientStore.getState().setUpdateClientLoading(false);
      }
    },
    [
      updateClientMutation,
      clients,
      nextToken,
      selectedClient,
      setClients,
      setSelectedClient,
      resetErrors,
    ]
  );

  /**
   * Suspend a client
   */
  const suspendClient = useCallback(
    async (clientId: string): Promise<any> => {
      try {
        const { data } = await suspendClientMutation({
          variables: { clientId },
          refetchQueries: [
            { query: LIST_CLIENTS },
            { query: GET_CLIENT, variables: { clientId } },
          ],
        });

        if (data?.suspendClient) {
          // Update the client in the store
          const updatedClients = clients.map((client) =>
            client.id === data.suspendClient.id ? data.suspendClient : client
          );
          setClients(updatedClients, nextToken);

          // Update selected client if it's the same one
          if (selectedClient?.id === data.suspendClient.id) {
            setSelectedClient(data.suspendClient);
          }

          return data.suspendClient;
        }
      } catch (error) {
        console.error("Error suspending client:", error);
        throw error;
      }
    },
    [
      suspendClientMutation,
      clients,
      nextToken,
      selectedClient,
      setClients,
      setSelectedClient,
    ]
  );

  /**
   * Activate a client
   */
  const activateClient = useCallback(
    async (clientId: string): Promise<any> => {
      try {
        const { data } = await activateClientMutation({
          variables: { clientId },
          refetchQueries: [
            { query: LIST_CLIENTS },
            { query: GET_CLIENT, variables: { clientId } },
          ],
        });

        if (data?.activateClient) {
          // Update the client in the store
          const updatedClients = clients.map((client) =>
            client.id === data.activateClient.id ? data.activateClient : client
          );
          setClients(updatedClients, nextToken);

          // Update selected client if it's the same one
          if (selectedClient?.id === data.activateClient.id) {
            setSelectedClient(data.activateClient);
          }

          return data.activateClient;
        }
      } catch (error) {
        console.error("Error activating client:", error);
        throw error;
      }
    },
    [
      activateClientMutation,
      clients,
      nextToken,
      selectedClient,
      setClients,
      setSelectedClient,
    ]
  );

  /**
   * Refresh clients list
   */
  const refreshClients = useCallback(() => {
    resetClientList();
    loadClients(true);
  }, [loadClients, resetClientList]);

  /**
   * Clear selected client
   */
  const clearSelectedClient = useCallback(() => {
    resetSelectedClient();
  }, [resetSelectedClient]);

  return {
    // State
    clients,
    clientsLoading,
    clientsError,
    hasMore,
    nextToken,
    selectedClient,
    selectedClientLoading,
    selectedClientError,

    // Actions
    loadClients,
    loadClient,
    loadClientStatistics,
    createClient,
    updateClient,
    suspendClient,
    activateClient,
    refreshClients,
    clearSelectedClient,

    // Utilities
    resetErrors,
  };
}

/**
 * Hook for client list operations
 */
interface ClientListReturn {
  clients: any[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  nextToken: string | null;
  loadClients: (reset?: boolean) => Promise<void>;
  refreshClients: () => void;
}

export function useClientList(): ClientListReturn {
  const service = useClientService();

  return {
    clients: service.clients,
    loading: service.clientsLoading,
    error: service.clientsError,
    hasMore: service.hasMore,
    nextToken: service.nextToken,
    loadClients: service.loadClients,
    refreshClients: service.refreshClients,
  };
}

/**
 * Hook for single client operations
 */
interface ClientDetailReturn {
  client: any | null;
  loading: boolean;
  error: string | null;
  loadClient: (clientId: string) => Promise<void>;
  updateClient: (input: UpdateClientInput) => Promise<any>;
  suspendClient: (clientId: string) => Promise<any>;
  activateClient: (clientId: string) => Promise<any>;
  clearClient: () => void;
}

export function useClientDetail(clientId?: string): ClientDetailReturn {
  const service = useClientService();

  // Auto-load client when clientId changes
  useEffect(() => {
    if (clientId && clientId !== service.selectedClient?.id) {
      service.loadClient(clientId);
    }
  }, [clientId, service.loadClient, service.selectedClient?.id]);

  return {
    client: service.selectedClient,
    loading: service.selectedClientLoading,
    error: service.selectedClientError,
    loadClient: service.loadClient,
    updateClient: service.updateClient,
    suspendClient: service.suspendClient,
    activateClient: service.activateClient,
    clearClient: service.clearSelectedClient,
  };
}
